import React, { useState, useEffect } from 'react';
import { Input, Button, Space, message } from 'antd';
import { BoldOutlined, ItalicOutlined, UnderlineOutlined, LinkOutlined, UnorderedListOutlined, OrderedListOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const RichTextEditor = ({ 
    value = '', 
    onChange, 
    placeholder = 'Enter description...',
    rows = 4,
    disabled = false,
    className = ''
}) => {
    const [content, setContent] = useState(value);
    const [isPreview, setIsPreview] = useState(false);

    useEffect(() => {
        setContent(value);
    }, [value]);

    const handleContentChange = (newContent) => {
        setContent(newContent);
        if (onChange) {
            onChange(newContent);
        }
    };

    const applyFormat = (format) => {
        const textarea = document.querySelector(`.rich-text-editor textarea`);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = content.substring(start, end);
        
        let formattedText = '';
        let newContent = '';
        
        switch (format) {
            case 'bold':
                formattedText = `**${selectedText}**`;
                break;
            case 'italic':
                formattedText = `*${selectedText}*`;
                break;
            case 'underline':
                formattedText = `<u>${selectedText}</u>`;
                break;
            case 'link':
                const url = prompt('Enter URL:');
                if (url) {
                    formattedText = `[${selectedText}](${url})`;
                } else {
                    return;
                }
                break;
            case 'bullet':
                formattedText = `• ${selectedText}`;
                break;
            case 'number':
                formattedText = `1. ${selectedText}`;
                break;
            default:
                formattedText = selectedText;
        }
        
        newContent = content.substring(0, start) + formattedText + content.substring(end);
        handleContentChange(newContent);
        
        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
        }, 0);
    };

    const renderPreview = (text) => {
        if (!text) return '';
        
        // Convert markdown-like formatting to HTML
        let html = text
            // Bold
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Italic
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            // Underline
            .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
            // Links
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
            // Line breaks
            .replace(/\n/g, '<br>')
            // Bullet points
            .replace(/^• (.*$)/gm, '<li>$1</li>')
            // Numbered lists
            .replace(/^\d+\. (.*$)/gm, '<li>$1</li>');
        
        // Wrap consecutive <li> elements in <ul>
        html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
        
        return html;
    };

    return (
        <div className={`rich-text-editor ${className}`}>
            <div className="mb-2">
                <Space wrap>
                    <Button 
                        size="small" 
                        icon={<BoldOutlined />} 
                        onClick={() => applyFormat('bold')}
                        title="Bold"
                    />
                    <Button 
                        size="small" 
                        icon={<ItalicOutlined />} 
                        onClick={() => applyFormat('italic')}
                        title="Italic"
                    />
                    <Button 
                        size="small" 
                        icon={<UnderlineOutlined />} 
                        onClick={() => applyFormat('underline')}
                        title="Underline"
                    />
                    <Button 
                        size="small" 
                        icon={<LinkOutlined />} 
                        onClick={() => applyFormat('link')}
                        title="Add Link"
                    />
                    <Button 
                        size="small" 
                        icon={<UnorderedListOutlined />} 
                        onClick={() => applyFormat('bullet')}
                        title="Bullet Point"
                    />
                    <Button 
                        size="small" 
                        icon={<OrderedListOutlined />} 
                        onClick={() => applyFormat('number')}
                        title="Numbered List"
                    />
                    <Button 
                        size="small" 
                        type={isPreview ? 'primary' : 'default'}
                        onClick={() => setIsPreview(!isPreview)}
                    >
                        {isPreview ? 'Edit' : 'Preview'}
                    </Button>
                </Space>
            </div>
            
            {isPreview ? (
                <div 
                    className="border border-gray-300 rounded p-3 bg-gray-50 min-h-[100px]"
                    dangerouslySetInnerHTML={{ __html: renderPreview(content) }}
                />
            ) : (
                <TextArea
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder={placeholder}
                    rows={rows}
                    disabled={disabled}
                    className="w-full"
                />
            )}
            
            <div className="mt-2 text-xs text-gray-500">
                <strong>Formatting:</strong> **bold**, *italic*, &lt;u&gt;underline&lt;/u&gt;, [link text](url), • bullet points, 1. numbered lists
            </div>
        </div>
    );
};

export default RichTextEditor;
